/**
 * `GET /metrics/executive` as the pages read it: every field present.
 *
 * The generated types mark every field with a server default as optional, so a page
 * would need a fallback at every read. The answer is normalised once here instead,
 * treated as untrusted: a missing number is 0, a missing share is null, a missing list
 * is empty, an unknown status reads as "not enough data". Shares stay fractions (0 to 1).
 */

import type {
  ExecArea,
  ExecDecision,
  ExecShare,
  ExecutiveMetrics,
  ExecValue,
  ExecWeek,
} from "./generated";

/** A type with every optional field required (null stays allowed where it was). */
export type Complete<T> = T extends readonly (infer U)[]
  ? Complete<U>[]
  : T extends object
    ? { [K in keyof T]-?: Complete<Exclude<T[K], undefined>> }
    : T;

export type Executive = Complete<ExecutiveMetrics>;
export type ExecutiveShare = Complete<ExecShare>;
export type ExecutiveArea = Complete<ExecArea>;
export type ExecutiveWeek = Complete<ExecWeek>;
export type ExecutiveDecision = Complete<ExecDecision>;
export type ExecutiveValue = Complete<ExecValue>;

const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;
const opt = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const list = <T>(value: readonly T[] | null | undefined): T[] =>
  Array.isArray(value) ? [...value] : [];

export function toShare(raw: ExecShare | null | undefined): ExecutiveShare {
  return {
    count: num(raw?.count),
    base: num(raw?.base),
    share: opt(raw?.share),
    lower_bound: opt(raw?.lower_bound),
    enough: raw?.enough === true,
    prev_count: num(raw?.prev_count),
    prev_base: num(raw?.prev_base),
    prev_share: opt(raw?.prev_share),
  };
}

const GATES = ["meets", "below", "not_enough", "feed_problem"] as const;
const STATES = ["on", "off", "not_measured", "not_started", "open"] as const;
const HEALTH = ["ok", "degraded", "stale"] as const;

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function toArea(raw: ExecArea): ExecutiveArea {
  return {
    area: String(raw.area ?? "").toLowerCase(),
    label: raw.label || String(raw.area ?? "").toUpperCase(),
    inquiries: num(raw.inquiries),
    quotations: num(raw.quotations),
    thread: toShare(raw.thread),
    single: toShare(raw.single),
    precision_items: toShare(raw.precision_items),
    precision_customer: toShare(raw.precision_customer),
    lines_auto: toShare(raw.lines_auto),
    truth_coverage: toShare(raw.truth_coverage),
    gate: oneOf(raw.gate, GATES, "not_enough"),
    trend: list(raw.trend).map(opt),
  };
}

function toWeek(raw: ExecWeek): ExecutiveWeek {
  return {
    week: String(raw.week ?? ""),
    partial: raw.partial === true,
    inquiries: num(raw.inquiries),
    ready: num(raw.ready),
    lines_auto: num(raw.lines_auto),
    lines_total: num(raw.lines_total),
    quotations: num(raw.quotations),
    lines: num(raw.lines),
    thread_share: opt(raw.thread_share),
    single_share: opt(raw.single_share),
    precision_right: num(raw.precision_right),
    precision_total: num(raw.precision_total),
    precision_share: opt(raw.precision_share),
    one_click_right: num(raw.one_click_right),
    one_click_checked: num(raw.one_click_checked),
  };
}

/** The executive answer with every field present (see the module comment). */
export function toExecutive(raw: ExecutiveMetrics): Executive {
  const h = raw.headline;
  const a = h?.agreement;
  const r = a?.revision;
  const s = h?.speed;
  return {
    computed_at: String(raw.computed_at ?? ""),
    period: {
      start: raw.period?.start ?? "",
      end: raw.period?.end ?? "",
      weeks: opt(raw.period?.weeks),
      month: str(raw.period?.month),
    },
    previous_period: {
      start: raw.previous_period?.start ?? "",
      end: raw.previous_period?.end ?? "",
      weeks: opt(raw.previous_period?.weeks),
      month: str(raw.previous_period?.month),
    },
    trend_weeks: num(raw.trend_weeks),
    areas_in_scope: list(raw.areas_in_scope),
    measured_areas: list(raw.measured_areas),
    headline: {
      agreement: {
        quotations: num(a?.quotations),
        lines: num(a?.lines),
        thread: toShare(a?.thread),
        single: toShare(a?.single),
        prev_quotations: num(a?.prev_quotations),
        revision: {
          measured: r?.measured === true,
          new_share: opt(r?.new_share),
          new_quotations: num(r?.new_quotations),
          revision_share: opt(r?.revision_share),
          revision_quotations: num(r?.revision_quotations),
          reason: str(r?.reason),
        },
      },
      precision: {
        items: toShare(h?.precision?.items),
        items_split: toShare(h?.precision?.items_split),
        customer: toShare(h?.precision?.customer),
      },
      inquiries: {
        count: num(h?.inquiries?.count),
        per_week: opt(h?.inquiries?.per_week),
        prev_count: num(h?.inquiries?.prev_count),
      },
      autonomy: {
        lines: toShare(h?.autonomy?.lines),
        inquiries_matched: toShare(h?.autonomy?.inquiries_matched),
        inquiries_ready: toShare(h?.autonomy?.inquiries_ready),
        right_in_fo: toShare(h?.autonomy?.right_in_fo),
      },
      speed: {
        prepared_n: num(s?.prepared_n),
        prepared_median_min: opt(s?.prepared_median_min),
        prepared_p90_min: opt(s?.prepared_p90_min),
        prev_prepared_median_min: opt(s?.prev_prepared_median_min),
        live_since: str(s?.live_since),
        reply_n: num(s?.reply_n),
        reply_median_h: opt(s?.reply_median_h),
        prev_reply_median_h: opt(s?.prev_reply_median_h),
        reply_areas: list(s?.reply_areas),
      },
    },
    value: list(raw.value).map((row) => ({
      area: String(row.area ?? "").toLowerCase(),
      currency: String(row.currency ?? "EUR"),
      quotations: num(row.quotations),
      quoted: num(row.quoted),
      won: num(row.won),
    })),
    weekly: list(raw.weekly).map(toWeek),
    areas: list(raw.areas).map(toArea),
    status: {
      health: oneOf(raw.status?.health, HEALTH, "ok"),
      import_stalled: raw.status?.import_stalled === true,
      last_import_at: str(raw.status?.last_import_at),
      master_data_ok: raw.status?.master_data_ok !== false,
      master_data_oldest_sync_at: str(raw.status?.master_data_oldest_sync_at),
      send_blocked: raw.status?.send_blocked !== false,
    },
    decisions: list(raw.decisions).map((entry) => ({
      key: String(entry.key ?? ""),
      state: oneOf(entry.state, STATES, "open"),
      text: String(entry.text ?? ""),
      effect_count: opt(entry.effect_count),
    })),
    flags: {
      pilot_started: raw.flags?.pilot_started === true,
      revision_split_measured: raw.flags?.revision_split_measured === true,
      value_available: raw.flags?.value_available === true,
    },
    gate: {
      precision_bar: num(raw.gate?.precision_bar) || 0.995,
      coverage_bar: num(raw.gate?.coverage_bar) || 0.75,
      min_lines: num(raw.gate?.min_lines) || 100,
      min_quotations: num(raw.gate?.min_quotations) || 30,
      set_on: raw.gate?.set_on ?? "Aug 2026",
    },
  };
}
